<template>
  <div>
    <v-dialog v-model="dialogVisible" max-width="540px">
      <v-card flat>
      <v-card-title class="d-flex align-center py-3 px-5" style="border-bottom: 1px solid rgba(0,0,0,0.08);">
        <span class="text-subtitle-1 font-weight-medium">Historial de aprobaciones</span>
        <v-spacer></v-spacer>
        <v-btn icon x-small @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-skeleton-loader v-if="loading" type="list-item-avatar-two-line@3" class="pa-4"></v-skeleton-loader>

        <v-alert v-else-if="error" type="error" outlined dense class="ma-4">
          {{ error }}
        </v-alert>

        <template v-else-if="history.length > 0">
          <div
            v-for="(item, index) in history"
            :key="index"
            class="d-flex align-start px-5 py-3"
            :style="index < history.length - 1 ? 'border-bottom: 1px solid rgba(0,0,0,0.06)' : ''"
          >
            <!-- Avatar -->
            <v-avatar size="32" :color="getAvatarColor(item.estado)" class="mr-3 mt-1 flex-shrink-0">
              <v-img v-if="item.user.info && item.user.info.avatar" :src="item.user.info.avatar"></v-img>
              <span v-else class="white--text" style="font-size: 11px; font-weight: 500;">
                {{ getInitials(item.user.name) }}
              </span>
            </v-avatar>

            <!-- Content -->
            <div style="flex: 1; min-width: 0;">
              <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                <span class="text-body-2 font-weight-medium">{{ item.user.name }}</span>
                <v-chip x-small :color="getStatusColor(item.estado)" text-color="white" label>
                  {{ item.estado }}
                </v-chip>
              </div>

              <div class="text-caption grey--text mt-1">
                {{ formatDateTime(item.created_at) }}
                <span v-if="item.subject && item.subject.name"> · {{ item.subject.name }}</span>
                <span v-else-if="item.subject_id === null"> · ITO</span>
              </div>

              <div v-if="item.comentario" class="text-caption mt-1" style="color: var(--v-theme-on-surface);">
                {{ item.comentario }}
              </div>

              <!-- Image preview replaces the classic attachment row. -->
              <div v-if="item.attachment && isImageAttachment(item.attachment)" class="mt-2">
                <button
                  type="button"
                  class="approval-history-image-preview"
                  :disabled="previewingFile === item.attachment.id"
                  :aria-label="`Previsualizar ${item.attachment.file.name}`"
                  @click="previewFile(item.attachment)"
                >
                  <v-img
                    :src="getAttachmentMediaPath(item.attachment)"
                    height="64"
                    width="88"
                    contain
                  >
                    <template v-slot:placeholder>
                      <div class="d-flex align-center justify-center fill-height">
                        <v-progress-circular indeterminate size="18" width="2" color="primary" />
                      </div>
                    </template>
                  </v-img>
                </button>
              </div>

              <!-- Classic attachment row for non-image files. -->
              <div
                v-else-if="item.attachment"
                class="d-flex align-center mt-2 px-2 py-1"
                style="background: rgba(0,0,0,0.03); border-radius: 6px; border: 1px solid rgba(0,0,0,0.08); gap: 8px;"
              >
                <v-icon :color="getFileIconColor(item.attachment?.file?.mime_type)" size="16">
                  {{ getFileIcon(item.attachment?.file?.mime_type) }}
                </v-icon>
                <span class="text-caption text-truncate flex-grow-1 grey--text text--darken-1">
                  {{ item.attachment?.file?.name }}
                </span>
                <span class="text-caption grey--text flex-shrink-0">
                  {{ formatFileSize(item.attachment?.file?.size) }}
                </span>
                <v-btn
                  icon
                  x-small
                  color="primary"
                  :loading="previewingFile === item.attachment.id"
                  @click="previewFile(item.attachment)"
                >
                  <v-icon size="16">mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  color="primary"
                  :loading="loadingFile === item.attachment.id"
                  @click="downloadFile(item.attachment)"
                >
                  <v-icon size="16">mdi-download</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="text-center py-8">
          <v-icon size="36" color="grey lighten-2">mdi-calendar-check-outline</v-icon>
          <p class="mt-2 text-caption grey--text">Sin historial de aprobaciones</p>
        </div>
      </v-card-text>
      </v-card>
    </v-dialog>

    <GlobalFileViewer
      :media-items="viewerMediaItems"
      :media-index="viewerMediaIndex"
      :document-item="viewerDocumentItem"
      :forge-item="viewerForgeItem"
      :hide-open-morphable="true"
      :hide-minimize="true"
      @close="clearViewers"
      @minimize-file="clearViewers"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { getApprovalHistory, getApprovalAttachment } from '@/helpers/api/entrega';
import GlobalFileViewer from '@/components/Globals/Helpers/GlobalFileViewer';

export default {
  name: 'ApprovalHistoryModal',

  components: {
    GlobalFileViewer
  },

  props: {
    taskId: {
      type: [Number, String],
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      history: [],
      loadingFile: null,
      previewingFile: null,
      viewerMediaItems: [],
      viewerMediaIndex: null,
      viewerDocumentItem: null,
      viewerForgeItem: null
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    }
  },

  watch: {
    show(newVal) {
      if (newVal && this.taskId) {
        this.fetchApprovalHistory();
      } else if (!newVal) {
        this.clearViewers();
      }
    }
  },

  methods: {
    close() {
      this.clearViewers();
      this.$emit('close');
    },

    clearViewers() {
      this.viewerMediaItems = [];
      this.viewerMediaIndex = null;
      this.viewerDocumentItem = null;
      this.viewerForgeItem = null;
      this.previewingFile = null;
    },

    getInitials(name) {
      if (!name) return '?';
      return name
        .split(/\s/)
        .reduce((acc, word) => acc + word.slice(0, 1), '')
        .slice(0, 2)
        .toUpperCase();
    },

    formatDateTime(dateTime) {
      return moment(dateTime).format('DD/MM/YYYY HH:mm');
    },

    getStatusColor(status) {
      const colors = {
        'Observado': 'amber darken-1',
        'Aprobado': 'green',
        'Rechazado': 'red',
        'Aprobado con Observaciones': 'blue'
      };
      return colors[status] || 'grey';
    },

    getAvatarColor(status) {
      const colors = {
        'Observado': 'amber darken-1',
        'Aprobado': 'green',
        'Rechazado': 'red',
        'Aprobado con Observaciones': 'blue'
      };
      return colors[status] || 'grey';
    },

    getFileIcon(mimeType) {
      if (!mimeType) return 'mdi-file-outline';
      if (mimeType.includes('image/')) return 'mdi-file-image-outline';
      if (mimeType.includes('application/pdf')) return 'mdi-file-pdf-outline';
      if (mimeType.includes('application/msword') || mimeType.includes('wordprocessingml')) return 'mdi-file-word-outline';
      if (mimeType.includes('application/vnd.ms-excel') || mimeType.includes('spreadsheetml')) return 'mdi-file-excel-outline';
      if (mimeType.includes('text/')) return 'mdi-file-document-outline';
      return 'mdi-file-outline';
    },

    getFileIconColor(mimeType) {
      if (!mimeType) return 'grey';
      if (mimeType.includes('image/')) return 'green';
      if (mimeType.includes('application/pdf')) return 'red';
      if (mimeType.includes('application/msword') || mimeType.includes('wordprocessingml')) return 'blue';
      if (mimeType.includes('application/vnd.ms-excel') || mimeType.includes('spreadsheetml')) return 'green darken-2';
      if (mimeType.includes('text/')) return 'blue-grey';
      return 'grey';
    },

    isImageAttachment(attachment) {
      const mimeType = attachment?.file?.mime_type || attachment?.file?.pathmedia?.mimetype;
      return Boolean(attachment?.file?.pathmedia?.path && mimeType?.includes('image/'));
    },

    getAttachmentMediaPath(attachment) {
      return attachment?.file?.pathmedia?.path || '';
    },

    formatFileSize(size) {
      if (!size) return '';
      const kb = size / 1024;
      if (kb < 1024) return `${Math.round(kb * 10) / 10} KB`;
      return `${Math.round((kb / 1024) * 10) / 10} MB`;
    },

    async previewFile(attachment) {
      if (!attachment?.id) return;
      this.previewingFile = attachment.id;
      this.clearViewers();
      this.previewingFile = attachment.id;

      try {
        const response = await getApprovalAttachment({ attachment_id: attachment.id });
        const file = response.code === 200 ? response.attachment : null;

        if (!file?.url) {
          this.showErrorMessage('No se puede previsualizar el archivo');
          return;
        }

        if (file.pathmedia?.path) {
          this.viewerMediaItems = [{
            src: file.pathmedia.path,
            downloadable_url: file.url,
            mime_type: file.pathmedia.mimetype || file.type,
            ext: file.ext,
            size: file.size,
            name: file.name,
            item: file
          }];
          this.viewerMediaIndex = 0;
        } else if (file.file_type === 'forge' || (file.forge_file?.translated && file.forge_file?.urn)) {
          this.viewerForgeItem = file;
        } else if (file.pathdoc?.path) {
          this.viewerDocumentItem = file;
        } else {
          this.showErrorMessage('Este archivo no tiene un visor disponible');
        }
      } catch (error) {
        console.error('Error previewing approval file:', error);
        this.showErrorMessage('Error al abrir el archivo');
      } finally {
        this.previewingFile = null;
      }
    },

    async downloadFile(attachment) {
      if (!attachment?.id) return;
      this.loadingFile = attachment.id;
      try {
        const response = await getApprovalAttachment({ attachment_id: attachment.id });
        if (response.code === 200 && response.attachment?.url) {
          window.open(response.attachment.url, '_blank');
        } else {
          this.showErrorMessage('No se pudo descargar el archivo');
        }
      } catch (error) {
        console.error('Error downloading file:', error);
        this.showErrorMessage('Error al descargar el archivo');
      } finally {
        this.loadingFile = null;
      }
    },

    showErrorMessage(message) {
      this.$store.globalStore.dispatch('addMessage', {
        message,
        title: 'Error',
        created: new Date(),
        type: 'error',
        icon: 'mdi-alert-circle',
        color: '#F44336'
      });
    },

    async fetchApprovalHistory() {
      if (!this.taskId) return;
      this.loading = true;
      this.error = null;
      try {
        const response = await getApprovalHistory({ task_id: this.taskId });
        if (response.code === 200) {
          this.history = response.history;
        } else {
          this.error = 'Error al cargar el historial de aprobaciones';
        }
      } catch (error) {
        console.error('Error fetching approval history:', error);
        this.error = 'Error de conexión al cargar el historial';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.approval-history-image-preview {
  width: 88px;
  height: 64px;
  flex: 0 0 88px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.04);
  cursor: pointer;
}

.approval-history-image-preview:hover {
  border-color: var(--v-primary-base);
}

.approval-history-image-preview:disabled {
  cursor: wait;
  opacity: 0.7;
}
</style>